import { type ContractDataFieldSectionDto, type ContractReportQueryDto } from "../../../autogen/bff-api";
import { base64Decode, base64Encode } from "../../../common/utils/base64";
import { withEncodedFilters } from "../../../common/saved-reports/savedReportLists";
import { withCurrentChoices } from "./customFieldChoices";

type Sections = { [key: string]: ContractDataFieldSectionDto } | null | undefined;

export type RemovedCustomField = { id: string; name: string | null };

const CUSTOM_FIELD_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const removedCustomFields = (
  q: Partial<ContractReportQueryDto> | null,
  currentSections: Sections,
  opened: { columns?: string[]; sortByCustomField?: string | null } = {}
): RemovedCustomField[] => {
  const current = new Set(Object.values(currentSections ?? {}).flatMap((section) => section.fields.map((f) => f.id)));
  const named = Object.values(q?.visibleCustomFields ?? {})
    .flatMap((section) => section.fields)
    .filter((field) => !current.has(field.id))
    .map(({ id, name }): RemovedCustomField => ({ id, name }));
  const unnamed = [
    ...(q?.customFieldSearch ?? []).map((search) => search.fieldId),
    ...(q?.customFieldSelect ?? []).map((select) => select.fieldId),
    ...(q?.customFieldValues ?? []).map((chosen) => chosen.fieldId),
    ...(opened.columns ?? []).filter((key) => CUSTOM_FIELD_ID.test(key)),
    ...(opened.sortByCustomField ? [opened.sortByCustomField] : []),
  ]
    .filter((id, index, ids) => !current.has(id) && ids.indexOf(id) === index)
    .filter((id) => !named.some((field) => field.id === id))
    .map((id): RemovedCustomField => ({ id, name: null }));
  return [...named, ...unnamed];
};

const withoutCustomFieldFilters = (
  q: Partial<ContractReportQueryDto>,
  removed: RemovedCustomField[]
): Partial<ContractReportQueryDto> => {
  if (removed.length === 0) return q;
  const gone = new Set(removed.map((field) => field.id));
  const search = q.customFieldSearch?.filter((config) => !gone.has(config.fieldId));
  const select = q.customFieldSelect?.filter((config) => !gone.has(config.fieldId));
  const values = q.customFieldValues?.filter((chosen) => !gone.has(chosen.fieldId));
  return {
    ...q,
    customFieldSearch: search?.length ? search : null,
    customFieldSelect: select?.length ? select : null,
    customFieldValues: values?.length ? values : null,
  };
};

const withoutColumns = (encodedColumns: string | null, removed: RemovedCustomField[]): string | null => {
  if (!encodedColumns || removed.length === 0) return encodedColumns;
  const gone = new Set(removed.map((field) => field.id));
  const columns = JSON.parse(base64Decode(encodedColumns)) as string[];
  return base64Encode(JSON.stringify(columns.filter((key) => !gone.has(key))));
};

export const upToDateContractReportQuery =
  (removed: RemovedCustomField[], currentSections: Sections) =>
  (search: string): string => {
    const params = new URLSearchParams(
      withEncodedFilters<Partial<ContractReportQueryDto>>(search, (q) =>
        withCurrentChoices(withoutCustomFieldFilters(withoutFieldDefinitions(q), removed), currentSections)
      )
    );
    const columns = withoutColumns(params.get("columns"), removed);
    if (columns) params.set("columns", columns);
    if (removed.some((field) => field.id === params.get("sortByCustomField"))) {
      params.delete("sortBy");
      params.delete("sortDirection");
      params.delete("sortByCustomField");
    }
    return params.toString();
  };

const withoutFieldDefinitions = ({
  visibleCustomFields: _definitions,
  ...q
}: Partial<ContractReportQueryDto>): Partial<ContractReportQueryDto> => q;
